import useGet from '../../../core/composables/api/useGet'
import usePost from '../../../core/composables/api/usePost'
import { onMounted, ref } from 'vue'
import type { FeedbackTopicType, SkillVisualization } from '@/core/models/feedback-topic'
import type { Perspective } from '@/core/models/perspective'
import { useDialog } from '@/core/components/base/dialog/use-dialog'
import type { EvaluationAnalysis, SurveyPerspectiveSummary } from '@/core/models/surveys-analytics'

export const useEvaluationProfile = (feedbackTopicId: string) => {
  const { data, status, load, reload } = useGet<EvaluationProfile>(
    `profile/evaluation/${feedbackTopicId}`,
  )

  const selectedSurveyIds = ref<string[]>([])

  const {
    data: analysisData,
    status: analysisStatus,
    mutate: loadAnalysis,
  } = usePost<EvaluationAnalysis, { surveyIds: string[] }>(
    `profile/evaluation/${feedbackTopicId}/analysis`,
  )

  onMounted(async () => {
    await load()
    if (status.value === 'success') {
      const allSurveyIds = data.value?.surveys.map((survey) => survey.id) ?? []
      selectedSurveyIds.value = allSurveyIds
      loadAnalysis({ surveyIds: allSurveyIds })
    }
  })

  const { openDialog, isOpen, closeDialog } = useDialog()

  const applySelectedSurveys = (surveyIds: string[]) => {
    closeDialog()
    selectedSurveyIds.value = [...surveyIds]
    loadAnalysis({ surveyIds: selectedSurveyIds.value })
  }

  return {
    data,
    status,
    load,
    reload,
    analysisData,
    analysisStatus,
    applySelectedSurveys,
    openDialog,
    isOpen,
    closeDialog,
    selectedSurveyIds,
  }
}

export interface EvaluationProfile {
  readonly title: string
  readonly id: string
  readonly type: FeedbackTopicType
  readonly icon?: string
  readonly perspectives: ReadonlyArray<Perspective>
  readonly includeSelf: boolean
  readonly surveys: ReadonlyArray<SurveySelectionPreview>
  readonly skillVisualization: ReadonlyArray<SkillVisualization>
  readonly showBlindspots: boolean
}

export interface SurveySelectionPreview {
  readonly id: string
  readonly title?: string
  readonly updatedAt: string
  readonly isFinished: boolean
  readonly perspectives: ReadonlyArray<SurveyPerspectiveSummary>
  readonly numberOfQuestions: number
}
